import React, { createContext, useContext, useEffect, useState } from "react";
import { TestRequestDisplayedState } from "../components/Chip/StatusChip";

export type StatusFilterState = {
  [Status in TestRequestDisplayedState]: boolean;
};

interface StatusFilterContextType {
  handleFilterChange: (status: TestRequestDisplayedState, active: boolean) => void;
  query: string;
  resetFilterState: (state?: StatusFilterState) => void;
  statusFilterState: StatusFilterState;
}

const initialFilterState: StatusFilterState = {
  CANCELED: false,
  COMPLETED: false,
  DECLINED: false,
  DRAFT: false,
  FOR_APPROVAL: false,
  FOR_EVALUATION: false,
  FOR_REVIEW: false,
  IN_EXECUTION: false,
  IN_PREPARATION: false,
  REPORT_CREATION: false,
  SUBMITTED: false,
};

const StatusFilterContext = createContext<StatusFilterContextType | undefined>(undefined);

export const StatusFilterContextProvider: React.FC<React.PropsWithChildren<Record<string, unknown>>> = (props) => {
  const { children } = props;
  const [filterState, setFilterState] = useState<StatusFilterState>(initialFilterState);

  const createQuery = (): string => {
    const queryElements: string[] = [];
    Object.entries(filterState).forEach(([status, active]) => {
      if (active) {
        queryElements.push(`states=${status}`);
      }
    });
    if (queryElements.length) {
      return "&".concat(queryElements.join("&"));
    }
    return "";
  };

  const handleFilterChange = (status: string, active: boolean) => {
    setFilterState((prevFilters) => ({ ...prevFilters, [status]: active }));
  };

  const query = createQuery();

  const resetFilterState = (state?: StatusFilterState) => {
    setFilterState(state || initialFilterState);
  };

  return (
    <StatusFilterContext.Provider
      value={{
        handleFilterChange,
        query,
        resetFilterState,
        statusFilterState: filterState,
      }}
    >
      {children}
    </StatusFilterContext.Provider>
  );
};

function useStatusFilter(initialState?: StatusFilterState): StatusFilterContextType {
  const context = useContext(StatusFilterContext);
  if (!context) {
    throw new Error("Wrap useSelectedColumns in a SelectedColumnsProvider.");
  }
  useEffect(() => {
    context.resetFilterState(initialState);
  }, []);
  return context;
}

export { StatusFilterContext, useStatusFilter };
